import React from 'react';
import type { MissingCostBasisItem } from '../../api/tax';
import { formatUsd, formatCrypto, formatDate, formatOrderId } from './taxReportingUtils';

// =============================================================================
// InboxRowContext — canonical context block for missing-cost-basis inbox rows.
// Reused by the chooser, the intentional-unresolved panel, and (via the
// `source: 'inbox'` mode) by `ManualTransactionModal` and `ManualTaxLotModal`.
//
// Renders portfolio, product, occurrence date, quantity, USD value, source
// order ID, and — when present (ZERO_COST_ACQUISITION rows, REQ-012) —
// the Coinbase acquisition order ID.
//
// Lays the fields out via the canonical `.detail-rows` / `.detail-row` /
// `.detail-label` / `.detail-value` idiom (TradeGroupDetail.css). Values that
// also appear in `CoinbaseTransactionsView` wear the same cell classes
// (`.date-cell`, `.order-id-cell`) so visual styling matches the tables.
// =============================================================================

export interface InboxRowContextProps {
  item: MissingCostBasisItem;
}

const InboxRowContext: React.FC<InboxRowContextProps> = ({ item }) => {
  return (
    <div className="detail-rows" aria-label="Inbox row context">
      <div className="detail-row">
        <span className="detail-label">Portfolio</span>
        <span className="detail-value">{item.portfolio_id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Product</span>
        <span className="detail-value">{item.product_id}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Occurrence Date</span>
        <span className="detail-value date-cell">{formatDate(item.occurrence_date)}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Quantity</span>
        <span className="detail-value">{formatCrypto(item.quantity)}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">USD Value</span>
        <span className="detail-value">{formatUsd(item.usd_value)}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Source Order ID</span>
        <span className="detail-value order-id-cell">{formatOrderId(item.source_order_id)}</span>
      </div>
      {item.acquisition_order_id && (
        <div className="detail-row">
          <span className="detail-label">Acquisition Order ID</span>
          <span className="detail-value order-id-cell">
            {formatOrderId(item.acquisition_order_id)}
          </span>
        </div>
      )}
    </div>
  );
};

export default InboxRowContext;
export { InboxRowContext };
